import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCustomers, deleteCustomer } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import { Edit2, Trash2, Plus, Search, MoreHorizontal, User, Users, Phone, Mail, Calendar, Filter, Eye, Download, Map, PieChart, BarChart } from 'lucide-react';

interface Customer {
  id: string;
  name: string;
  phone: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  customer_type?: string;
  notes?: string;
  last_order_date?: string;
  created_at: string;
  orders_count?: number;
  total_spent?: number;
}

// Função para exportar dados para CSV
const exportToCSV = (customers: Customer[]) => {
  // Cabeçalhos do CSV
  const headers = [
    'Nome',
    'Telefone',
    'Email',
    'Endereço',
    'Cidade',
    'Estado',
    'Tipo',
    'Data de Cadastro',
    'Último Pedido',
    'Total de Pedidos',
    'Total Gasto'
  ];
  
  // Converter dados para linhas do CSV
  const csvRows = [
    headers.join(','), // Linha de cabeçalho
    ...customers.map(customer => [
      `"${customer.name || ''}"`,
      `"${customer.phone || ''}"`,
      `"${customer.email || ''}"`,
      `"${customer.address || ''}"`,
      `"${customer.city || ''}"`,
      `"${customer.state || ''}"`,
      `"${customer.customer_type === 'pessoa_juridica' ? 'Jurídica' : 'Física'}"`,
      `"${customer.created_at ? new Date(customer.created_at).toLocaleDateString('pt-BR') : ''}"`,
      `"${customer.last_order_date ? new Date(customer.last_order_date).toLocaleDateString('pt-BR') : ''}"`,
      `"${customer.orders_count || 0}"`,
      `"${formatCurrency(customer.total_spent).replace('R$', '').trim()}"`,
    ].join(','))
  ];
  
  // Criar blob e fazer download
  const csvString = csvRows.join('\n');
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `clientes_${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Substituir por um componente DIY simples
const ProgressBar = ({ value = 0, className = "", ...props }: { value: number, className?: string }) => {
  return (
    <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`} {...props}>
      <div 
        className="bg-primary h-full rounded-full transition-all" 
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
};

const CustomersPage = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('lista');
  
  const customersPerPage = 10;
  const navigate = useNavigate();

  const loadCustomers = async (search?: string) => {
    try {
      setIsLoading(true);
      const data = await fetchCustomers(search);
      setCustomers(data);
      applyFilters(data);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      toast.error('Erro ao carregar lista de clientes');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  const applyFilters = (customersData: Customer[] = customers) => {
    let result = [...customersData];

    // Aplicar filtro por tipo
    if (filter !== 'all') {
      result = result.filter(customer => {
        if (filter === 'with_orders') {
          return customer.orders_count && customer.orders_count > 0;
        } else if (filter === 'no_orders') {
          return !customer.orders_count || customer.orders_count === 0;
        } else if (filter === 'pessoa_fisica' || filter === 'pessoa_juridica') {
          return customer.customer_type === filter;
        }
        return true;
      });
    }

    // Aplicar ordenação
    result.sort((a, b) => {
      let valueA, valueB;

      switch (sortBy) {
        case 'name':
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
          break;
        case 'created_at':
          valueA = new Date(a.created_at).getTime();
          valueB = new Date(b.created_at).getTime();
          break;
        case 'last_order_date':
          valueA = a.last_order_date ? new Date(a.last_order_date).getTime() : 0;
          valueB = b.last_order_date ? new Date(b.last_order_date).getTime() : 0;
          break;
        case 'orders_count':
          valueA = a.orders_count || 0;
          valueB = b.orders_count || 0;
          break;
        case 'total_spent':
          valueA = a.total_spent || 0;
          valueB = b.total_spent || 0;
          break;
        default:
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return valueA > valueB ? 1 : -1;
      } else {
        return valueA < valueB ? 1 : -1;
      }
    });

    setFilteredCustomers(result);
    setCurrentPage(1);
  };

  useEffect(() => {
    applyFilters();
  }, [filter, sortBy, sortOrder]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadCustomers(searchTerm);
  };

  const handleFilterChange = (value: string) => {
    setFilter(value);
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
  };

  const handleSortOrderToggle = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleViewCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowDetails(true);
  };

  const handleDeleteCustomer = async (id: string, name: string) => {
    if (window.confirm(`Tem certeza que deseja excluir o cliente "${name}"?`)) {
      try {
        await deleteCustomer(id);
        toast.success(`Cliente "${name}" excluído com sucesso`);
        loadCustomers(searchTerm); // Recarregar a lista
      } catch (error) {
        console.error('Erro ao excluir cliente:', error);
        toast.error('Erro ao excluir cliente');
      }
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Paginação
  const indexOfLastCustomer = currentPage * customersPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);
  const totalPages = Math.ceil(filteredCustomers.length / customersPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Estatísticas
  const totalCustomers = customers.length;
  const customersWithOrders = customers.filter(c => c.orders_count && c.orders_count > 0).length;
  const totalOrdersAmount = customers.reduce((sum, customer) => sum + (customer.total_spent || 0), 0);
  const averageOrderValue = totalOrdersAmount / (customersWithOrders || 1);
  
  // Dados para gráficos
  const customersByType = {
    fisica: customers.filter(c => c.customer_type === 'pessoa_fisica').length,
    juridica: customers.filter(c => c.customer_type === 'pessoa_juridica').length,
    undefined: customers.filter(c => !c.customer_type).length,
  };
  
  // Agrupar clientes por estado
  const customersByState = customers.reduce((acc: Record<string, number>, customer) => {
    if (customer.state) {
      acc[customer.state] = (acc[customer.state] || 0) + 1;
    }
    return acc;
  }, {});
  
  // Top clientes por gastos
  const topSpendingCustomers = [...customers]
    .filter(c => c.total_spent && c.total_spent > 0)
    .sort((a, b) => (b.total_spent || 0) - (a.total_spent || 0))
    .slice(0, 5);

  return (
    <DashboardLayout title="Clientes">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="lista">
              <Users className="h-4 w-4 mr-2" />
              Lista de Clientes
            </TabsTrigger>
            <TabsTrigger value="relatorios">
              <PieChart className="h-4 w-4 mr-2" />
              Relatórios e Estatísticas
            </TabsTrigger>
            <TabsTrigger value="distribuicao" disabled={customers.filter(c => c.state).length === 0}>
              <Map className="h-4 w-4 mr-2" />
              Distribuição Geográfica
            </TabsTrigger>
          </TabsList>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => exportToCSV(customers)}>
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </Button>
            <Button onClick={() => navigate('/dashboard/customers/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Cliente
            </Button>
          </div>
        </div>

        <TabsContent value="lista" className="space-y-6">
          {/* Estatísticas rápidas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center">
                  <User className="h-8 w-8 text-primary mb-2" />
                  <p className="text-2xl font-bold">{totalCustomers}</p>
                  <p className="text-muted-foreground text-sm">Total de Clientes</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center">
                  <Users className="h-8 w-8 text-primary mb-2" />
                  <p className="text-2xl font-bold">{customersWithOrders}</p>
                  <p className="text-muted-foreground text-sm">Clientes com Pedidos</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center">
                  <Calendar className="h-8 w-8 text-primary mb-2" />
                  <p className="text-2xl font-bold">{formatCurrency(totalOrdersAmount)}</p>
                  <p className="text-muted-foreground text-sm">Total em Vendas</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center">
                  <Mail className="h-8 w-8 text-primary mb-2" />
                  <p className="text-2xl font-bold">{formatCurrency(averageOrderValue)}</p>
                  <p className="text-muted-foreground text-sm">Ticket Médio</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filtros e botões */}
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div className="flex flex-col md:flex-row gap-4">
              <form onSubmit={handleSearch} className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Buscar por nome, telefone ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="md:w-64"
                />
                <Button type="submit" variant="secondary" size="icon">
                  <Search className="h-4 w-4" />
                </Button>
              </form>

              <div className="flex gap-2">
                <Select onValueChange={handleFilterChange} value={filter}>
                  <SelectTrigger className="w-40">
                    <div className="flex items-center">
                      <Filter className="mr-2 h-4 w-4" />
                      <SelectValue placeholder="Filtrar por" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Clientes</SelectItem>
                    <SelectItem value="with_orders">Com Pedidos</SelectItem>
                    <SelectItem value="no_orders">Sem Pedidos</SelectItem>
                    <SelectItem value="pessoa_fisica">Pessoa Física</SelectItem>
                    <SelectItem value="pessoa_juridica">Pessoa Jurídica</SelectItem>
                  </SelectContent>
                </Select>

                <Select onValueChange={handleSortChange} value={sortBy}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Nome</SelectItem>
                    <SelectItem value="created_at">Data de Cadastro</SelectItem>
                    <SelectItem value="last_order_date">Último Pedido</SelectItem>
                    <SelectItem value="orders_count">Número de Pedidos</SelectItem>
                    <SelectItem value="total_spent">Total Gasto</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="outline" onClick={handleSortOrderToggle}>
                  {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
                </Button>
              </div>
            </div>
          </div>

          {/* Tabela de clientes */}
          {isLoading ? (
            <div className="text-center py-8">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
              <p className="mt-2 text-muted-foreground">Carregando clientes...</p>
            </div>
          ) : currentCustomers.length > 0 ? (
            <>
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Contato</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Pedidos</TableHead>
                        <TableHead>Último Pedido</TableHead>
                        <TableHead>Cadastro</TableHead>
                        <TableHead>Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentCustomers.map((customer) => (
                        <TableRow key={customer.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center">
                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center mr-2 text-primary">
                                {customer.name.charAt(0).toUpperCase()}
                              </div>
                              {customer.name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center text-sm">
                                <Phone className="h-3 w-3 mr-1 text-muted-foreground" />
                                {customer.phone}
                              </div>
                              {customer.email && (
                                <div className="flex items-center text-sm text-muted-foreground">
                                  <Mail className="h-3 w-3 mr-1" />
                                  {customer.email}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={customer.customer_type === 'pessoa_juridica' ? 'secondary' : 'outline'}>
                              {customer.customer_type === 'pessoa_juridica' ? 'Jurídica' : 'Física'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span>{customer.orders_count || 0} pedido(s)</span>
                              <span className="text-sm text-muted-foreground">
                                {formatCurrency(customer.total_spent)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>{formatDate(customer.last_order_date)}</TableCell>
                          <TableCell>{formatDate(customer.created_at)}</TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Ações</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleViewCustomer(customer)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  Ver Detalhes
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate(`/dashboard/customers/edit/${customer.id}`)}>
                                  <Edit2 className="mr-2 h-4 w-4" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteCustomer(customer.id, customer.name)}
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Paginação */}
              {totalPages > 1 && (
                <Pagination className="justify-end">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : ""} 
                      />
                    </PaginationItem>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Mostrar primeira página, última página e 2 páginas em torno da atual
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <PaginationItem key={page}>
                            <PaginationLink
                              isActive={page === currentPage}
                              onClick={() => paginate(page)}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      }
                      
                      // Mostrar elipses entre números
                      if (
                        (page === 2 && currentPage > 3) ||
                        (page === totalPages - 1 && currentPage < totalPages - 2)
                      ) {
                        return (
                          <PaginationItem key={page}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      
                      return null;
                    })}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
                        className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""} 
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          ) : (
            <div className="text-center py-12 bg-muted rounded-lg">
              <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Nenhum cliente encontrado</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm 
                  ? `Não encontramos clientes para "${searchTerm}".` 
                  : 'Não há clientes cadastrados no sistema.'}
              </p>
              <Button onClick={() => navigate('/dashboard/customers/new')}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar Cliente
              </Button>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="relatorios" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Clientes por Tipo</CardTitle>
                <CardDescription>Distribuição entre pessoas físicas e jurídicas</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Pessoa Física</span>
                    <span className="text-sm text-muted-foreground">{customersByType.fisica} ({Math.round(customersByType.fisica / totalCustomers * 100) || 0}%)</span>
                  </div>
                  <ProgressBar value={Math.round(customersByType.fisica / totalCustomers * 100) || 0} className="h-2" />
                  
                  <div className="flex items-center justify-between mt-6">
                    <span className="text-sm font-medium">Pessoa Jurídica</span>
                    <span className="text-sm text-muted-foreground">{customersByType.juridica} ({Math.round(customersByType.juridica / totalCustomers * 100) || 0}%)</span>
                  </div>
                  <ProgressBar value={Math.round(customersByType.juridica / totalCustomers * 100) || 0} className="h-2" />
                  
                  {customersByType.undefined > 0 && (
                    <>
                      <div className="flex items-center justify-between mt-6">
                        <span className="text-sm font-medium">Não definido</span>
                        <span className="text-sm text-muted-foreground">{customersByType.undefined} ({Math.round(customersByType.undefined / totalCustomers * 100) || 0}%)</span>
                      </div>
                      <ProgressBar value={Math.round(customersByType.undefined / totalCustomers * 100) || 0} className="h-2" />
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Status de Engajamento</CardTitle>
                <CardDescription>Clientes com e sem pedidos no sistema</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Com Pedidos</span>
                    <span className="text-sm text-muted-foreground">{customersWithOrders} ({Math.round(customersWithOrders / totalCustomers * 100) || 0}%)</span>
                  </div>
                  <ProgressBar value={Math.round(customersWithOrders / totalCustomers * 100) || 0} className="h-2" />
                  
                  <div className="flex items-center justify-between mt-6">
                    <span className="text-sm font-medium">Sem Pedidos</span>
                    <span className="text-sm text-muted-foreground">{totalCustomers - customersWithOrders} ({Math.round((totalCustomers - customersWithOrders) / totalCustomers * 100) || 0}%)</span>
                  </div>
                  <ProgressBar value={Math.round((totalCustomers - customersWithOrders) / totalCustomers * 100) || 0} className="h-2" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Top 5 Clientes (Por Valor Gasto)</CardTitle>
                <CardDescription>Clientes que mais contribuem em vendas</CardDescription>
              </CardHeader>
              <CardContent>
                {topSpendingCustomers.length > 0 ? (
                  <div className="space-y-4">
                    {topSpendingCustomers.map((customer, index) => (
                      <div key={customer.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-medium">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium">{customer.name}</p>
                            <p className="text-sm text-muted-foreground">{customer.phone}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{formatCurrency(customer.total_spent)}</p>
                          <p className="text-sm text-muted-foreground">{customer.orders_count} pedidos</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Não há dados de vendas para exibir
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex justify-end border-t pt-4">
                <Button variant="outline" size="sm" onClick={() => setActiveTab('lista')}>
                  Ver Todos os Clientes
                </Button>
              </CardFooter>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="distribuicao" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Distribuição Geográfica</CardTitle>
              <CardDescription>Visualize a distribuição dos clientes por estado</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(customersByState).length > 0 ? (
                <>
                  <div className="h-[300px] flex items-end gap-2 mt-6">
                    {Object.entries(customersByState)
                      .sort((a, b) => b[1] - a[1])
                      .map(([state, count]) => {
                        const percentage = Math.round((count / totalCustomers) * 100);
                        const height = Math.max(30, (count / Math.max(...Object.values(customersByState))) * 250);
                        
                        return (
                          <div key={state} className="flex-1 flex flex-col items-center">
                            <div 
                              className="w-full bg-primary/80 rounded-t-md transition-all hover:bg-primary" 
                              style={{ height: `${height}px` }}
                            ></div>
                            <div className="text-xs font-medium mt-2">{state}</div>
                            <div className="text-xs text-muted-foreground">{count} ({percentage}%)</div>
                          </div>
                        );
                      })}
                  </div>
                  <div className="mt-8">
                    <p className="text-sm text-muted-foreground">
                      Nota: Esta visualização representa somente os {Object.keys(customersByState).length} estados com clientes cadastrados.
                    </p>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Não existem dados de localização suficientes para exibir esta visualização.
                  Certifique-se de que os clientes tenham o campo "Estado" preenchido.
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-end border-t pt-4">
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/customers/new')}>
                Adicionar Novo Cliente
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de detalhes do cliente */}
      {showDetails && selectedCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">{selectedCustomer.name}</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowDetails(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Informações de Contato</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>{selectedCustomer.phone}</span>
                    </div>
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>{selectedCustomer.email || 'Não informado'}</span>
                    </div>
                    <Separator />
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Endereço:</p>
                      <p>{selectedCustomer.address || 'Não informado'}</p>
                      {selectedCustomer.city && selectedCustomer.state && (
                        <p>{selectedCustomer.city} - {selectedCustomer.state}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Informações de Pedidos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-3 bg-muted rounded-md">
                        <p className="text-sm text-muted-foreground">Total de Pedidos</p>
                        <p className="text-lg font-bold">{selectedCustomer.orders_count || 0}</p>
                      </div>
                      <div className="p-3 bg-muted rounded-md">
                        <p className="text-sm text-muted-foreground">Valor Total</p>
                        <p className="text-lg font-bold">{formatCurrency(selectedCustomer.total_spent)}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Último Pedido:</p>
                      <p>{formatDate(selectedCustomer.last_order_date)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Observações</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    {selectedCustomer.notes || 'Nenhuma observação registrada para este cliente.'}
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowDetails(false)}>
                Fechar
              </Button>
              <Button onClick={() => {
                setShowDetails(false);
                navigate(`/dashboard/customers/edit/${selectedCustomer.id}`);
              }}>
                <Edit2 className="mr-2 h-4 w-4" />
                Editar Cliente
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CustomersPage; 